/**
 * units.js — single source of truth for unit rendering.
 *
 * Backend serves weather in metric (°C, mm, km/h) and distance in miles.
 * The UI renders imperial across the board (RF-T01).  All Store/DC weather
 * surfaces should use these helpers so we never mix unit systems on the
 * same card or page.
 *
 * Each formatter is null-safe: pass `null` / `undefined` and you get `'--'`
 * (or whatever `fallback` you provide) instead of `'NaN°F'`.
 */

const FALLBACK = '--'

const isNumber = (v) => typeof v === 'number' && Number.isFinite(v)

// ── Conversion primitives ───────────────────────────────────────────────────

export function celsiusToFahrenheit(c) {
  if (!isNumber(c)) return null
  return (c * 9) / 5 + 32
}

export function kmhToMph(kmh) {
  if (!isNumber(kmh)) return null
  return kmh * 0.621371
}

export function mmToInches(mm) {
  if (!isNumber(mm)) return null
  return mm / 25.4
}

export function cmToInches(cm) {
  if (!isNumber(cm)) return null
  return cm / 2.54
}

// ── Display formatters ──────────────────────────────────────────────────────

/** "72°F" — round to nearest whole degree. */
export function formatTempF(tempC, { fallback = FALLBACK } = {}) {
  const f = celsiusToFahrenheit(tempC)
  return f === null ? fallback : `${Math.round(f)}°F`
}

/** "65–82°F" — both ends in F, single unit. */
export function formatTempRangeF(minC, maxC, { fallback = FALLBACK } = {}) {
  const lo = celsiusToFahrenheit(minC)
  const hi = celsiusToFahrenheit(maxC)
  if (lo === null && hi === null) return fallback
  if (lo === null) return `${Math.round(hi)}°F`
  if (hi === null) return `${Math.round(lo)}°F`
  return `${Math.round(lo)}–${Math.round(hi)}°F`
}

/** "12 mph" — one decimal under 10 mph, integer above. */
export function formatWindMph(kmh, { fallback = FALLBACK } = {}) {
  const mph = kmhToMph(kmh)
  if (mph === null) return fallback
  return mph < 10 ? `${mph.toFixed(1)} mph` : `${Math.round(mph)} mph`
}

/** "0.4 in" / "1.2 in" — precipitation in inches. */
export function formatPrecipIn(mm, { fallback = FALLBACK } = {}) {
  const inches = mmToInches(mm)
  if (inches === null) return fallback
  return `${inches.toFixed(inches < 1 ? 2 : 1)} in`
}

/** "3.1 in" — snowfall in inches (input is cm). */
export function formatSnowIn(cm, { fallback = FALLBACK } = {}) {
  const inches = cmToInches(cm)
  if (inches === null) return fallback
  return `${inches.toFixed(inches < 1 ? 2 : 1)} in`
}

/** "1,234 mi" — backend distance is already in miles. */
export function formatDistanceMi(mi, { fallback = FALLBACK } = {}) {
  if (!isNumber(mi)) return fallback
  return `${Math.round(mi).toLocaleString()} mi`
}
